import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Autocomplete } from '../Base/Form'
import { postData } from '../../../js/helpers/apiHelper'
import NetworkPanel from './NetworkPanel'

// Debounce function to limit API calls
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null)

  return useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args)
      }, delay)
    },
    [callback, delay]
  )
}

const SelectLocation = ({
  name,
  label,
  control,
  error,
  setValue,
  handleFocus,
  watch,
  disabled = false,
  locationSearchApi,
  locationFindApi,
  dictionary,
}) => {
  const [options, setOptions] = useState([])
  const [inputValue, setInputValue] = useState()
  const [showResult, setShowResults] = useState(false)
  const [showNetworkPanel, setShowNetworkPanel] = useState(false)
  const [inputFocus, setInputFocus] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const locationRef = useRef()
  const value = watch(name)

  // Search function to be debounced
  const performSearch = useCallback(
    (searchTerm) => {
      postData(locationSearchApi, { searchTerm })
        .then((response) => {
          setOptions(
            response.data.records?.map((record) => ({
              value: record.Code,
              country: record.Country,
              text: record.Address1,
            })) || []
          )
        })
        .finally(() => {
          setIsSearching(false)
        })
    },
    [locationSearchApi]
  )

  // Create debounced search function
  const debouncedSearch = useDebounce(performSearch, 400)

  useEffect(() => {
    // Call debounced search function when input value changes
    if (inputValue?.length > 2) {
      setIsSearching(true)
      debouncedSearch(inputValue)
    } else {
      setOptions([])
    }
  }, [inputValue, debouncedSearch])

  useEffect(() => {
    const handleBodyClick = (event) => {
      if (
        locationRef.current &&
        !locationRef.current.contains(event.target) &&
        !event.target.closest('.b-select-location') &&
        !event.target.classList.contains('js-prevent-propagation')
      ) {
        setShowNetworkPanel(false)
      }
    }

    document.body.addEventListener('click', handleBodyClick)

    return () => {
      document.body.removeEventListener('click', handleBodyClick)
    }
  }, [])

  // handle show/hide panels on typing
  useEffect(() => {
    if (inputFocus) {
      if (inputValue?.length > 0 && inputValue !== value?.text) {
        setShowResults(true)
        setShowNetworkPanel(false)
      } else if (inputValue?.length === 0) {
        setShowResults(false)
        setShowNetworkPanel(true)
      }
    }
  }, [inputValue, inputFocus, value?.text])

  const handleSelectNetworkLocation = (location) => {
    const newLocation = {
      value: location.value,
      country: location.country,
      text: `${
        /^[A-Za-z]{3}$/.test(location.oagCode) ? `${location.oagCode}, ` : ''
      }${location.text}, ${location.city}, ${location.countryName}`,
    }
    setValue(name, newLocation, { shouldValidate: true })
  }

  // hide both panels on select value
  useEffect(() => {
    if (value) {
      setShowNetworkPanel(false)
      setShowResults(false)
    }
  }, [value])

  useEffect(() => {
    if (handleFocus) {
      handleFocus(showNetworkPanel || showResult)
    }
  }, [showNetworkPanel, showResult, handleFocus])

  const handleInputChange = (value) => {
    setInputValue(value)
  }

  return (
    <div className="b-select-location" ref={locationRef}>
      <Autocomplete
        name={name}
        label={label}
        control={control}
        options={options}
        error={error}
        onInputChange={handleInputChange}
        noOptionsText={dictionary.validation?.location}
        onSearchingText={
          isSearching
            ? dictionary.locationSearchingText
            : dictionary.locationNotFound
        }
        disableSearch={true}
        onFocus={() => {
          setShowNetworkPanel(true)
          setInputFocus(true)
        }}
        onBlur={() => {
          setInputFocus(false)
          setShowResults(false)
        }}
        open={showResult}
        disabled={disabled}
        loading={isSearching}
      />
      {showNetworkPanel && (
        <div className="b-select-location__network">
          <NetworkPanel
            onChange={handleSelectNetworkLocation}
            locationFindApi={locationFindApi}
            dictionary={dictionary}
          />
        </div>
      )}
    </div>
  )
}

export default SelectLocation
